import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { RefundServiceV2 } from '../../services/RefundServiceV2';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from './CustomSnackBar';
import { useAppSelector } from '../../store/hooks';
import GroupPermissionFunction from '../../utils/groupPermissionFunction';
import { UserGroupsEnum } from '../../enums/UserGroupsEnum';

interface PartialRefundDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    razorpayOrderId: number;
    maxRefundable: number;
}

const refundServiceV2 = new RefundServiceV2();

const PartialRefundDialog = (props: PartialRefundDialogProps) => {
    const { open, onClose, onSuccess, razorpayOrderId, maxRefundable } = props;
    const [amount, setAmount] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const userDetails = useAppSelector((state) => state.userDetails.value);
    const groupsArray: string[] = userDetails.adminUser.accessGroups;

    const handleClose = () => {
        setAmount('');
        onClose();
    };

    const handleSubmit = () => {
        if (!GroupPermissionFunction(groupsArray, [UserGroupsEnum.MANAGER, UserGroupsEnum.ADMIN])) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Only managers have access to initiate a refund',
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        if (!amount || Number(amount) <= 0) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Enter a valid refund amount',
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        if (Number(amount) > maxRefundable) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: `Amount exceeds refundable balance of ₹${maxRefundable}`,
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        setLoading(true);
        refundServiceV2
            .createRefundForOrder(razorpayOrderId, { refundAmount: amount })
            .then(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: 'Refund initiated successfully',
                    type: CustomSnackbarTypes.SUCCESS,
                });
                setLoading(false);
                setAmount('');
                onSuccess();
            })
            .catch(() => {
                setLoading(false);
            });
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Partial Refund</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Refundable amount: ₹{maxRefundable}
                </Typography>
                <TextField
                    fullWidth
                    label="Refund Amount (₹)"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    InputProps={{ inputProps: { min: 1, max: maxRefundable } }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="error">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PartialRefundDialog;
